import { useEffect, useMemo, useState } from 'react';
import type { EvaluationSummary, UploadResponse } from '../types/stats';
import { formatCount } from '../utils/format';
import { AnalyticsSegmentedControl } from '../../weld-analytics/components/shared/AnalyticsSegmentedControl';
import { EvaluationTabs } from './EvaluationTabs';
import { StatTable } from './StatTable';

const MeasurementTabs = ({ evaluation }: { evaluation: EvaluationSummary }) => {
  const [activeMeasurement, setActiveMeasurement] = useState<string>(
    evaluation.variables[0]?.variable ?? '',
  );

  const formatMeasurementLabel = (label: string) => label.replace(/\s*\(mm\)$/i, '').trim();

  if (!evaluation.variables.length) {
    return (
      <p className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-slate-500">
        No measurements available for this evaluation.
      </p>
    );
  }

  const current = evaluation.variables.find((variable) => variable.variable === activeMeasurement);

  return (
    <div className="space-y-4">
      <AnalyticsSegmentedControl
        ariaLabel="Measurement"
        tabs={evaluation.variables.map((variable) => ({
          id: variable.variable,
          label: formatMeasurementLabel(variable.variable),
        }))}
        activeId={activeMeasurement}
        onChange={setActiveMeasurement}
      />
      {current ? <StatTable summary={current} /> : null}
    </div>
  );
};

export function NdtStatisticsSection({ data }: { data: UploadResponse | null }) {
  const [selectedEvaluation, setSelectedEvaluation] = useState<string | null>(null);

  useEffect(() => {
    const evaluations = data?.evaluations;
    if (!evaluations?.length) return;
    const evaluationNames = evaluations.map((evaluation) => evaluation.evaluation);
    if (evaluationNames.length === 1) {
      setSelectedEvaluation(evaluationNames[0]);
      return;
    }
    if (!selectedEvaluation || !evaluationNames.includes(selectedEvaluation)) {
      const evaluationWithMostData = evaluations.reduce((prev, current) =>
        current.rowCount > prev.rowCount ? current : prev,
      );
      setSelectedEvaluation(evaluationWithMostData.evaluation);
    }
  }, [data, selectedEvaluation]);

  const currentEvaluation = useMemo(
    () => data?.evaluations?.find((evaluation) => evaluation.evaluation === selectedEvaluation) ?? null,
    [data, selectedEvaluation],
  );

  const evaluationStats = useMemo(() => {
    if (!currentEvaluation) return null;
    const referenceVariable = currentEvaluation.variables[0];
    if (!referenceVariable) {
      return {
        uniqueDefects: 0,
        totalRecords: currentEvaluation.rowCount,
        indications: 0,
        noIndications: currentEvaluation.rowCount,
      };
    }
    const rows = referenceVariable.rows;
    const uniqueDefects = rows.filter((row) => row.comment !== 'No Indication').length;
    const indications = rows.reduce((sum, row) => {
      if (row.comment === 'No Indication') return sum;
      return sum + row.totalCount;
    }, 0);
    const totalRecords = currentEvaluation.rowCount;
    const noIndications = totalRecords - indications;
    return { uniqueDefects, totalRecords, indications, noIndications };
  }, [currentEvaluation]);

  if (!data?.metadata || !Array.isArray(data.evaluations)) {
    return (
      <p className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-slate-500">
        No data available.
      </p>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-slate-500">Evaluations</p>
          <h2 className="text-lg font-semibold text-slate-900">Explore acceptance vs rejection trends</h2>
          {evaluationStats && (
            <p className="mt-2 text-xs text-slate-500">
              Unique defects:&nbsp;
              <span className="font-semibold text-slate-700">{evaluationStats.uniqueDefects}</span>
              &nbsp;• Total records:&nbsp;
              <span className="font-semibold text-slate-700">{formatCount(evaluationStats.totalRecords)}</span>
              &nbsp;• Indications:&nbsp;
              <span className="font-semibold text-slate-900">{formatCount(evaluationStats.indications)}</span>
              <span className="text-slate-500"> / {formatCount(evaluationStats.totalRecords)}</span>
              &nbsp;• No indications:&nbsp;
              <span className="font-semibold text-slate-900">{formatCount(evaluationStats.noIndications)}</span>
              <span className="text-slate-500"> / {formatCount(evaluationStats.totalRecords)}</span>
            </p>
          )}
        </div>
        <EvaluationTabs
          evaluations={data.evaluations}
          selected={selectedEvaluation}
          onSelect={(evaluationName) => {
            setSelectedEvaluation(evaluationName);
            const evaluationNames = data.evaluations.map((e) => e.evaluation);
            if (!evaluationName && evaluationNames.length > 0) {
              setSelectedEvaluation(evaluationNames[0]);
            }
          }}
        />
      </div>
      {currentEvaluation ? (
        <MeasurementTabs evaluation={currentEvaluation} />
      ) : (
        <p className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-slate-500">
          Select an evaluation above to display its statistics.
        </p>
      )}
    </>
  );
}
